/**
 * The three traced cases behind the OEM+ "Case-to-closure trace" page.
 *
 * Source: `const WORKFLOWS={ … }` at line 2334 of
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html` (3 cases:
 * `breakdown`, `predictive`, `seismic`).
 *
 * Porting decisions
 * - `machine` was `IDS.L14` / `IDS.J07` / `IDS.T22` in the demo. `IDS` is
 *   `B.ids` (line 1836), a display-id map; those references are resolved to the
 *   literal ids the demo renders: `L-02`, `J-04`, `T-09`. One task string was
 *   built by concatenation — `'Pads + labour to '+IDS.T22+' / Redgum B'` — and
 *   is stored resolved as `'Pads + labour to T-09 / Redgum B'`.
 * - Positional tuples became named-field interfaces, named from
 *   `renderWorkflow()` (line ~2352):
 *     phase: `[name, pct, status, steps]`      → `WorkflowPhase`
 *     step:  `[name, pct, status, tasks]`      → `WorkflowStep`
 *     task:  plain string, verbatim            → `WorkflowStep.tasks`
 *   The renderer splits a task on `' · '` for its bold lead and its detail, and
 *   regex-matches a technician name out of it; that stays renderer behaviour, so
 *   task strings are kept whole and verbatim.
 * - `status` is the closed set `'done' | 'now' | 'todo'` and drives both the pill
 *   copy (Completed / In progress / Not started) and the ring colour.
 * - `pct` is 0–100 and is averaged across phases for the overall figure.
 * - No markup and no handler strings existed inside this constant; every
 *   `show(...)` / `onclick` in the workflow view lives in the renderer.
 *
 * Data Schema layer — pure data, no behaviour.
 */

/** Progress state of a phase or a step. */
export type WorkflowStatus = 'done' | 'now' | 'todo';

/** A column on the task board: one work stream inside a phase. */
export interface WorkflowStep {
  /** Step name, the task-board column heading. */
  readonly name: string;
  /** Completion 0–100, drawn as the progress ring. */
  readonly pct: number;
  readonly status: WorkflowStatus;
  /** Task cards under the column, verbatim. */
  readonly tasks: readonly string[];
}

/** One phase of a traced case. */
export interface WorkflowPhase {
  /** Phase name, e.g. `'Parts & logistics'`. */
  readonly name: string;
  /** Completion 0–100; the overall figure is the mean across phases. */
  readonly pct: number;
  readonly status: WorkflowStatus;
  readonly steps: readonly WorkflowStep[];
}

/** One traced case. */
export interface Workflow {
  /** Case name, shown in the `<select>` and the hero. */
  readonly name: string;
  /** Machine id the case is against, e.g. `'L-02'`. */
  readonly machine: string;
  /** `'<case no> · <mine name>'`; the hero splits it on `' · '`. */
  readonly ref: string;
  readonly phases: readonly WorkflowPhase[];
}

/** Keys of `WORKFLOWS`, in demo (picker) order. */
export const WORKFLOW_KEYS = ['breakdown', 'predictive', 'seismic'] as const;

/** A traced-case key, e.g. `'seismic'`. */
export type WorkflowKey = (typeof WORKFLOW_KEYS)[number];

/** The three traced cases, keyed as in the demo. */
export const WORKFLOWS: Readonly<Record<WorkflowKey, Workflow>> = {
  breakdown: {
    name: 'Unplanned breakdown → return to service',
    machine: 'L-02',
    ref: 'WO-26-11842 · Kalbarri',
    phases: [
      {
        name: 'Detect',
        pct: 100,
        status: 'done',
        steps: [
          {
            name: 'Onboard alarm captured',
            pct: 100,
            status: 'done',
            tasks: ['E-HYD-PRESS-LOW logged 14:21:40', 'E-HYD-PUMP-DRIVE logged 14:21:52'],
          },
          {
            name: 'Operator report',
            pct: 100,
            status: 'done',
            tasks: ['Operator calls supervisor 14:22', 'Supervisor emails technician 14:31'],
          },
          {
            name: 'Data reaches platform',
            pct: 100,
            status: 'done',
            tasks: ['Shift-end export 18:05 (Pitram) — after the fact', 'Agent back-test flags 20 Aug drift'],
          },
        ],
      },
      {
        name: 'Dispatch & mobilise',
        pct: 100,
        status: 'done',
        steps: [
          {
            name: 'Assign technician',
            pct: 100,
            status: 'done',
            tasks: ['Dale Whitmore assigned', 'Travel from surface workshop 35 min'],
          },
          {
            name: 'Pre-load knowledge',
            pct: 100,
            status: 'done',
            tasks: ['SP-ML21-HYD-004 pushed to headset', 'LOTO-UG-003 pump-compartment steps'],
          },
        ],
      },
      {
        name: 'Diagnose',
        pct: 100,
        status: 'done',
        steps: [
          {
            name: 'Isolate & verify',
            pct: 100,
            status: 'done',
            tasks: ['Isolation 15:12, second person verified'],
          },
          {
            name: 'Inspect',
            pct: 100,
            status: 'done',
            tasks: ['Coupling sheared; pump OK by hand', 'Return filter cut: metal + elastomer'],
          },
          {
            name: 'Oil analysis',
            pct: 100,
            status: 'done',
            tasks: ['Sample taken 15:21 → ISO 22/19/16', 'Two overdue samples confirmed in SAP'],
          },
        ],
      },
      {
        name: 'Parts & logistics',
        pct: 100,
        status: 'done',
        steps: [
          {
            name: 'Site stock check',
            pct: 100,
            status: 'done',
            tasks: ['KG store: coupling 1 (on truck), oil 0 drums'],
          },
          {
            name: 'Urgent order',
            pct: 100,
            status: 'done',
            tasks: ['SO-90411 2 × coupling, Perth DC overnight A$640'],
          },
          {
            name: 'Consumables',
            pct: 100,
            status: 'done',
            tasks: ['2 × 205 L oil + filter cart from surface store 16:40'],
          },
        ],
      },
      {
        name: 'Repair',
        pct: 100,
        status: 'done',
        steps: [
          {
            name: 'Replace coupling',
            pct: 100,
            status: 'done',
            tasks: ['Hub gap 4.0 mm · pump bolts 210 N·m'],
          },
          {
            name: 'Contamination flush',
            pct: 100,
            status: 'done',
            tasks: ['3 µm cart, 2 drums, new return filter'],
          },
          { name: 'Test', pct: 100, status: 'done', tasks: ['Idle, 5 boom cycles, 30 min at temp'] },
        ],
      },
      {
        name: 'Return to service',
        pct: 100,
        status: 'done',
        steps: [
          {
            name: 'Post-repair sample',
            pct: 100,
            status: 'done',
            tasks: ['ISO 19/17/14 — follow-up at +50 h'],
          },
          { name: 'Hand back', pct: 100, status: 'done', tasks: ['RTS 20:46 · operator briefed on brow'] },
        ],
      },
      {
        name: 'Close-out & cost',
        pct: 90,
        status: 'now',
        steps: [
          { name: 'SAP PM close', pct: 100, status: 'done', tasks: ['WO closed with root cause and parts'] },
          {
            name: 'Cost ledger',
            pct: 100,
            status: 'done',
            tasks: ['A$15,250 all-in · attributed to KG Night B + operator practice'],
          },
          { name: 'Warranty', pct: 100, status: 'done', tasks: ['CS-2026-2210 declined — contamination'] },
          {
            name: 'Follow-up sample',
            pct: 60,
            status: 'now',
            tasks: ['Due at +50 h (≈ 11 Sep)', 'Task with Alicia Fenton'],
          },
        ],
      },
      {
        name: 'Learn',
        pct: 40,
        status: 'now',
        steps: [
          { name: 'Reliability review', pct: 100, status: 'done', tasks: ['Debrief 04 Sep · actions logged'] },
          { name: 'Bulletin', pct: 100, status: 'done', tasks: ['TB-2026-031 issued 08 Sep · 250 h sampling'] },
          {
            name: 'Fleet re-score',
            pct: 20,
            status: 'now',
            tasks: ['All ML-21 re-scored for overdue samples', 'Kalbarri joins pilot at L1'],
          },
        ],
      },
    ],
  },
  predictive: {
    name: 'Predictive alert → planned change-out',
    machine: 'J-04',
    ref: 'WO-26-11901 · Broken Ridge',
    phases: [
      {
        name: 'Detect signature',
        pct: 100,
        status: 'done',
        steps: [
          { name: 'Trend model', pct: 100, status: 'done', tasks: ['Percussion −8% / 9 d', 'Pre-charge drift'] },
          {
            name: 'Corroborate',
            pct: 100,
            status: 'done',
            tasks: ['Boom camera water leak', 'Shank 30 h overdue'],
          },
        ],
      },
      {
        name: 'Assess',
        pct: 100,
        status: 'done',
        steps: [
          { name: 'Confidence & window', pct: 100, status: 'done', tasks: ['0.79 · 60–110 percussion h'] },
          { name: 'Duty context', pct: 100, status: 'done', tasks: ['1840 rehab drives — heavy duty'] },
        ],
      },
      {
        name: 'Plan',
        pct: 100,
        status: 'done',
        steps: [
          { name: 'Draft WO', pct: 100, status: 'done', tasks: ['WO-26-11901 next cut change · 6 h'] },
          { name: 'Attach procedure', pct: 100, status: 'done', tasks: ['SP-MJ2D-RD-011 Rev C, torques'] },
          { name: 'Safety check', pct: 100, status: 'done', tasks: ['Accumulator discharge mandatory'] },
        ],
      },
      {
        name: 'Parts & logistics',
        pct: 60,
        status: 'now',
        steps: [
          { name: 'Stock position', pct: 100, status: 'done', tasks: ['BR 0 · Newcastle 0 · Perth 3'] },
          {
            name: 'Requisition',
            pct: 100,
            status: 'done',
            tasks: ['SO-90418 drafted, approved by planner'],
          },
          {
            name: 'Freight',
            pct: 40,
            status: 'now',
            tasks: ['Air from Perth, 3 d — ETA 11 Sep', 'Newcastle min level review (Tom Reilly)'],
          },
        ],
      },
      {
        name: 'Approve',
        pct: 100,
        status: 'done',
        steps: [
          { name: 'Planner approval', pct: 100, status: 'done', tasks: ['Marco Bellini 08 Sep 08:10'] },
          {
            name: 'Technician check',
            pct: 100,
            status: 'done',
            tasks: ['Ben Tuilagi: kit is enough, piston OK'],
          },
        ],
      },
      {
        name: 'Execute',
        pct: 0,
        status: 'todo',
        steps: [
          { name: 'Front-end change', pct: 0, status: 'todo', tasks: ['At cut change after kit lands'] },
          { name: 'Feed alignment', pct: 0, status: 'todo', tasks: ['One-sided shank wear → check'] },
        ],
      },
      {
        name: 'Verify',
        pct: 0,
        status: 'todo',
        steps: [{ name: 'Test', pct: 0, status: 'todo', tasks: ['190–205 bar, no leak'] }],
      },
      {
        name: 'Learn',
        pct: 0,
        status: 'todo',
        steps: [
          { name: 'Feed the model', pct: 0, status: 'todo', tasks: ['Percussion hours on removed front end'] },
        ],
      },
    ],
  },
  seismic: {
    name: 'Live alert → restriction (with seismic exclusion)',
    machine: 'T-09',
    ref: 'WO-26-11912 · Mount Isolde',
    phases: [
      {
        name: 'Live detect',
        pct: 100,
        status: 'done',
        steps: [
          { name: 'MQTT threshold', pct: 100, status: 'done', tasks: ['brake_temp_rr_c > 240 ×4 in shift'] },
          { name: 'Vision', pct: 100, status: 'done', tasks: ['Brake glow rear RH 04:39'] },
        ],
      },
      {
        name: 'Assess',
        pct: 100,
        status: 'done',
        steps: [
          {
            name: 'Procedure trigger',
            pct: 100,
            status: 'done',
            tasks: ['SP-MT63-BRK-002 §1: 4 events → inspect'],
          },
          { name: 'Wear context', pct: 100, status: 'done', tasks: ['Pads 82% at PM-500 28 Aug'] },
        ],
      },
      {
        name: 'Restrict',
        pct: 50,
        status: 'now',
        steps: [
          {
            name: 'Empty-haul restriction',
            pct: 50,
            status: 'now',
            tasks: ['Drafted to FMS — awaiting planner'],
          },
          { name: 'Operator brief', pct: 0, status: 'todo', tasks: ['Retarder 3, hold 14 km/h'] },
        ],
      },
      {
        name: 'Plan',
        pct: 50,
        status: 'now',
        steps: [
          { name: 'Draft WO', pct: 100, status: 'done', tasks: ['WO-26-11912 · 10:00 crib · Anders'] },
          { name: 'Parts', pct: 100, status: 'done', tasks: ['3 pad sets in MI store, 1 reserved'] },
          { name: 'Approve', pct: 0, status: 'todo', tasks: ['Jorge Castillo'] },
        ],
      },
      {
        name: 'Execute',
        pct: 0,
        status: 'todo',
        steps: [
          { name: 'Pad & disc inspection', pct: 0, status: 'todo', tasks: ['4-point pad, disc run-out'] },
          { name: 'Sensor check', pct: 0, status: 'todo', tasks: ['IR vs telemetry ±15 °C'] },
        ],
      },
      {
        name: 'Verify',
        pct: 0,
        status: 'todo',
        steps: [{ name: 'Road test', pct: 0, status: 'todo', tasks: ['Loaded descent < 220 °C'] }],
      },
      {
        name: 'Cost & attribute',
        pct: 30,
        status: 'now',
        steps: [
          {
            name: 'Seismic exclusion coding',
            pct: 100,
            status: 'done',
            tasks: ['07 Sep ML 1.8 → 6 h coded SEISMIC-EXCLUSION, ground-conditions cost centre'],
          },
          { name: 'Machine cost', pct: 0, status: 'todo', tasks: ['Pads + labour to T-09 / Redgum B'] },
        ],
      },
      {
        name: 'Learn',
        pct: 0,
        status: 'todo',
        steps: [
          { name: 'Decline model', pct: 0, status: 'todo', tasks: ['Ambient 34 °C at 1,450 → site threshold'] },
        ],
      },
    ],
  },
};

/** The case the trace page opens on (`renderWorkflow('breakdown')`). */
export const DEFAULT_WORKFLOW_KEY: WorkflowKey = 'breakdown';

/** Pill copy per status, as the demo renders it. */
export const WORKFLOW_STATUS_LABEL: Readonly<Record<WorkflowStatus, string>> = {
  done: 'Completed',
  now: 'In progress',
  todo: 'Not started',
};
